import { useNavigate } from "react-router-dom";
import { useCallback } from "react";
import {
	buildSDPSearchPayload,
	getLastSearchData,
	normalizeDate,
	pickInitialValue,
	hasCustomBookingOccupancies,
} from "app/utils/smartDPUtils";
import {
	HOTEL_ONLY_PRODUCTS_FILTERS_KEY,
	QUOTATION_CODE_STATUS,
	RESOLUTION,
	WEBSITE_TUNNEL_TYPES,
} from "app/constants";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { unsetSDPProductsFilter } from "app/pages/SmartDP/Listing/SmartDPFilters/smartDPProductsFilterActionCreators";
import HotelOnlySearchForm from "app/pages/HotelOnly/HotelOnlySearchForm/HotelOnlySearchForm";
import { resetHotelOnlyBookingFlowId } from "app/pages/HotelOnly/hotelOnlyActionCreator";
import { isEmpty, isNil } from "lodash";
import withRouter from "app/utils/hocs/withRouter";

const HotelOnlySearchFormContainer = props => {
	const {
		unsetSDPProductsFilter = () => {},
		resetHotelOnlyBookingFlowId = () => {},
		onSubmit,
	} = props;

	const navigate = useNavigate();

	const handleFormSubmission = useCallback(({ values, pathname = "" }) => {
		const queryParams = buildSDPSearchPayload(values);
		unsetSDPProductsFilter(HOTEL_ONLY_PRODUCTS_FILTERS_KEY.BUDGET);

		// Réinitialiser le bookingFlowId à chaque nouvelle recherche HotelOnly.
		resetHotelOnlyBookingFlowId();

		if (onSubmit) {
			onSubmit(values);
		}

		if (pathname.includes("/hotelonly/listing")) {
			navigate({ pathname: pathname, search: queryParams }, { replace: true });
		} else {
			navigate({ pathname: "/hotelonly/listing", search: queryParams });
		}
	}, []);

	return (
		<div className="search-form__container">
			<HotelOnlySearchForm {...props} onSuccess={handleFormSubmission} />
		</div>
	);
};

HotelOnlySearchFormContainer.propTypes = {
	fetchHotelOnlySearchFormAvailability: PropTypes.func,
	resetHotelOnlyBookingFlowId: PropTypes.func,
	unsetSDPProductsFilter: PropTypes.func,
	destinations: PropTypes.array,
	isMobile: PropTypes.bool,
	brand: PropTypes.string,
	disableFormPersistance: PropTypes.bool, // utilisé pour éviter le flicker des champs entre la page merch et la home SDP
	onSubmit: PropTypes.func,
};

const mapStateToProps = (state, props) => {
	const { booking } = state;
	const { disableFormPersistance } = props;
	const isMerchPage = props?.location?.pathname?.includes("/merch");
	const lastSearch = getLastSearchData(state, WEBSITE_TUNNEL_TYPES.HOTEL_ONLY);

	const initialValues = {
		travelDates: {
			departureDate: "",
			endDate: "",
		},
	};

	// En merch, la page fixe destination/dates mais on garde l'historique pour le reste.
	const canUseLastSearchForMainFields = !disableFormPersistance && !isMerchPage;
	const canUseLastSearchForOtherFields = !disableFormPersistance;

	// Petit helper pour garder la priorité booking -> requête -> dernière recherche sans répéter la config.
	const pickValue = params =>
		pickInitialValue({
			disableFormPersistance,
			...params,
		});

	const normalizeValue = date => (date ? normalizeDate(date) : undefined);

	// 1. On privilégie la destination persistée, sinon on retombe sur la dernière recherche.
	const destinationResort = pickValue({
		bookingValue: booking?.destinationResort,
		lastSearchValue: lastSearch?.destinationResort,
		allowLastSearch: canUseLastSearchForMainFields,
		isValueValid: value => !isEmpty(value),
	});

	const hasDestination = Boolean(destinationResort);

	if (hasDestination) {
		initialValues.destinationResort = destinationResort;
	}

	// 2. Dates et durée ne sont reprises que lorsque la destination est définie et la persistance active.
	if (hasDestination && !disableFormPersistance) {
		const departureDate = pickValue({
			bookingValue: normalizeValue(booking?.departureDate),
			lastSearchValue: normalizeValue(lastSearch?.departureDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (departureDate) {
			initialValues.travelDates.departureDate = departureDate;
		}

		const endDate = pickValue({
			bookingValue: normalizeValue(booking?.endDate),
			lastSearchValue: normalizeValue(lastSearch?.endDate),
			allowLastSearch: canUseLastSearchForMainFields,
		});

		if (endDate) {
			initialValues.travelDates.endDate = endDate;
		}

		const bookingDuration = booking?.duration?.value;
		const lastDuration = lastSearch?.duration?.value;

		const durationCode = pickValue({
			bookingValue: bookingDuration,
			lastSearchValue: lastDuration,
			allowLastSearch: canUseLastSearchForMainFields,
			isValueValid: value => !isNil(value),
		});

		if (!isNil(durationCode)) {
			initialValues.duration = { code: durationCode };
		}
	}

	// 3. On persiste les occupancies uniquement si l'utilisateur est sorti de la config par défaut.
	if (!disableFormPersistance) {
		const bookingOccupancies = hasCustomBookingOccupancies(booking)
			? booking?.occupancies
			: undefined;
		const lastSearchOccupancies =
			lastSearch?.occupancies?.length > 0 ? lastSearch?.occupancies : undefined;

		const occupancies = pickValue({
			bookingValue: bookingOccupancies,
			lastSearchValue: lastSearchOccupancies,
			allowLastSearch: canUseLastSearchForOtherFields,
			isValueValid: value => Array.isArray(value) && value.length > 0,
		});

		if (occupancies) {
			initialValues.occupancies = occupancies;
		}
	}

	return {
		initialValues,
		destinations: state.smartDP.destinations,
		validateOnMount: state.smartDP.status === QUOTATION_CODE_STATUS.BAD_REQUEST,
		isMobile: [RESOLUTION.SMALL, RESOLUTION.MEDIUM].includes(state?.resolution),
		brand: state.brand.code,
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			unsetSDPProductsFilter,
			resetHotelOnlyBookingFlowId,
		},
		dispatch
	);
};

export default withRouter(
	injectIntl(
		connect(
			mapStateToProps,
			mapDispatchToProps
		)(HotelOnlySearchFormContainer)
	)
);
